<template>
  <v-card flat class="rmr-table-shell gp-history-shell">
    <v-card-title class="rmr-table-shell__header">
      <div class="rmr-table-shell__topline">
        <div class="rmr-table-shell__heading">
          <div class="gp-history-shell__title-row">
            <div class="rmr-table-shell__title">Historique des publications</div>
            <span class="gp-history-shell__count-badge">{{ total }}</span>
          </div>
        </div>
        <div class="rmr-table-shell__controls">
          <div class="rmr-table-shell__filter-item gp-history-shell__filter">
            <label class="rmr-table-shell__filter-label">Marque</label>
            <rmr-autocomplete
              :value="filters.brand_id"
              :items="brandItems"
              item-text="brand_name"
              item-value="id"
              label="Filtrer par marque"
              single-line
              clearable
              @input="onFilterChange"
            />
          </div>

          <rmr-action-button
            label="Retour"
            :icon="icons.mdiArrowLeft"
            variant="ghost"
            @click="$emit('back')"
          />
        </div>
      </div>
    </v-card-title>

    <v-card-text class="rmr-table-shell__body">
      <v-data-table
        :headers="headers"
        :items="logs"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        class="rmr-data-table gp-history-table"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
      >
        <template v-slot:loading>
          <div class="rmr-data-table__state">
            <v-progress-circular indeterminate color="#6366F1" size="28" class="mb-2" />
            <div class="rmr-data-table__state-title">Chargement de l'historique</div>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="rmr-data-table__state">
            <v-icon color="#94A3B8" size="32" class="mb-2">{{ icons.mdiHistory }}</v-icon>
            <div class="rmr-data-table__state-title">
              {{ filters.brand_id ? "Aucun résultat" : "Aucune publication disponible" }}
            </div>
            <div class="rmr-data-table__state-text">
              {{
                filters.brand_id
                  ? "Aucune publication ne correspond au filtre de marque actuel."
                  : "Aucune publication n'a encore été enregistrée."
              }}
            </div>
          </div>
        </template>

        <template slot="item.brand_name" slot-scope="{ item }">
          <span class="gp-history-table__brand">{{ item.brand_name || "-" }}</span>
        </template>

        <template slot="item.published_at" slot-scope="{ item }">
          {{ formatDateTimeFrench(item.published_at) }}
        </template>

        <template slot="item.status" slot-scope="{ item }">
          <rmr-status-chip
            :label="item.status === 'success' ? 'Succès' : 'Échec'"
            :variant="item.status === 'success' ? 'success' : 'error'"
            size="sm"
          />
        </template>

        <template slot="item.error_message" slot-scope="{ item }">
          <span class="gp-history-table__error" :title="item.error_message || '-'">
            {{ item.error_message || "-" }}
          </span>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mdiArrowLeft, mdiHistory } from "@mdi/js";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrAutocomplete from "@/components/ui/forms/RmrAutocomplete.vue";
import RmrStatusChip from "@/components/ui/feedback/RmrStatusChip.vue";
import { formatDateTimeFrench } from "@/utils/dates";

export default {
  name: "GeneratePresentationsHistoryCard",
  components: {
    RmrActionButton,
    RmrAutocomplete,
    RmrStatusChip,
  },
  emits: ["back"],
  data() {
    return {
      logs: [],
      total: 0,
      loading: false,
      brandItems: [],
      filters: {
        brand_id: null,
      },
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      headers: [
        { text: "Marque", value: "brand_name", width: "260px" },
        { text: "Statut", value: "status", width: "120px" },
        { text: "Date", value: "published_at", width: "190px" },
        { text: "Publié par", value: "published_by", width: "180px" },
        { text: "Erreur", value: "error_message", sortable: false },
      ],
      icons: {
        mdiArrowLeft,
        mdiHistory,
      },
    };
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.fetchLogs();
      },
    },
  },
  mounted() {
    this.fetchBrandItems();
    this.fetchLogs();
  },
  methods: {
    formatDateTimeFrench,
    async fetchBrandItems() {
      try {
        const data = await this.$store.dispatch("marketing/fetchBrands", {
          page: 1,
          limit: 0,
          sort_by: "brand_name",
        });
        this.brandItems = data.items || [];
      } catch (error) {
        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: "Erreur lors du chargement des marques.",
          color: "error",
          timeout: 5000,
        });
      }
    },
    async fetchLogs() {
      this.loading = true;
      try {
        const params = {
          page: this.options.page,
          limit: this.options.itemsPerPage,
        };

        if (this.filters.brand_id) {
          params.brand_id = this.filters.brand_id;
        }

        const { items, total } = await this.$store.dispatch("marketing/fetchPublicationLogs", params);
        this.logs = items;
        this.total = total;
      } catch (error) {
        this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: "Erreur lors du chargement de l'historique.",
          color: "error",
          timeout: 5000,
        });
      } finally {
        this.loading = false;
      }
    },
    onFilterChange(value) {
      this.filters.brand_id = value;
      if (this.options.page !== 1) {
        this.options.page = 1;
        return;
      }
      this.fetchLogs();
    },
  },
};
</script>

<style lang="scss">
.gp-history-shell__title-row {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.gp-history-shell__count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 10px;
  border-radius: 999px;
  background: $rmr-indigo-bg;
  color: $rmr-indigo-dark;
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
}

.gp-history-shell__filter {
  max-width: 260px;
}

.gp-history-table__brand {
  font-weight: 700;
  color: $rmr-text-secondary;
}

.gp-history-table__error {
  color: $rmr-text-secondary;
}

@media (max-width: 960px) {
  .gp-history-shell__filter {
    max-width: 100%;
    width: 100%;
  }
}
</style>
